import React, { useEffect, useState } from 'react';
import {
  Stack,
  Button,
  Typography,
  Box,
  Grow,
  Slide,
  useTheme,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Product } from '@domain/product/models/Product';
import { ProductFilter, ProductStatus } from '@domain/product/models/ProductFilter';

import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import CombineProductsBottomSheet from '../create/CombineProductsBottomSheet';
import BulkEditBar from './bulkEditBar';
import { useProducts } from '../../hooks/useProducts';
import DialogHeader from '@shared/ui/dialog/DialogHeader';

interface BulkActionBarProps {
  selectedProducts: Product[];
  products: Product[];
  setSelectedProducts: (products: Product[]) => void;
  totalCount?: number;
  status?: ProductStatus;
  filter?: ProductFilter;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedProducts,
  setSelectedProducts,
  totalCount,
  products,
  status = ProductStatus.ACTIVE,
  filter,
}) => {
  const theme = useTheme();

  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const [isAllSelected, setIsAllSelected] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [bulkEditBarOpen, setBulkEditBarOpen] = useState(false);

  const hasSelection = isAllSelected || selectedProducts.length > 0;

  const { bulkEditProducts } = useProducts();

  useEffect(() => {
    if (selectedProducts.length === 0) {
      setBulkEditBarOpen(false);
    }
  }, [selectedProducts]);

  const clearSelection = () => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  };

  const handleSelectAll = () => {
    setSelectedProducts(products);
    setIsAllSelected(true);
  };

  const isArchivedTab = status === ProductStatus.ARCHIVED;

  const handleArchiveClick = () => {
    const filters = isAllSelected ? filter : { productIds: selectedProducts.map((p) => p.id) };
    bulkEditProducts({
      updateData: isArchivedTab
        ? { active: true }
        : { active: false },
      filters,
    });
    setArchiveDialogOpen(false);
  };

  const handleBundleClick = () => {
    openBottomSheet({
      title: 'Combine Products',
      component: (
        <CombineProductsBottomSheet
          selectedProducts={selectedProducts}
          onSuccess={() => {
            setSelectedProducts([]);
            setIsAllSelected(false);
          }}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  return (
    <>
      <Box sx={{ flexShrink: 0}}>
        <Grow in={hasSelection}>
          <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'flex-end' }}>
            <Slide direction="left" in={bulkEditBarOpen}>
              <Box sx={{ position: 'absolute', right: 0, top: 0, zIndex: 2 }}>
                <BulkEditBar selectedProducts={isAllSelected ? undefined : selectedProducts} products={products} filter={filter} onClose={() => setBulkEditBarOpen(false)} />
              </Box>
            </Slide>

            <Slide direction="left" in={!bulkEditBarOpen}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {isAllSelected ? totalCount : selectedProducts.length}/{totalCount} product{totalCount !== 1 ? 's' : ''}
                  </Typography>

                  {totalCount != null && totalCount > selectedProducts.length && !isAllSelected && (
                    <Button variant="text" onClick={handleSelectAll} sx={{ whiteSpace: 'nowrap' }}>
                      Select All
                    </Button>
                  )}

                  <Button variant="text" onClick={clearSelection} sx={{ whiteSpace: 'nowrap' }}>
                    Clear
                  </Button>

                  <Stack direction="row" spacing={1} alignItems="center"
                    sx={{
                      pointerEvents: 'auto',
                      color: 'white',
                      backgroundColor: theme.palette.primary.main,
                      p: 0.5,
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: `0px 0px 16px 0px ${theme.palette.primary.main}33`,
                    }}
                  >
                    <Button variant="text" onClick={handleBundleClick} color="inherit">
                      Bundle
                    </Button>
                    <Button variant="text" color="inherit" onClick={() => setBulkEditBarOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                      Edit
                    </Button>
                    <Button variant="text" color="inherit" onClick={() => setArchiveDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                      {isArchivedTab ? 'Restore' : 'Archive'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Slide>
          </Box>
        </Grow>
      </Box>

      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogHeader>
          <Typography variant="h6">{isArchivedTab ? 'Restore Products' : 'Archive Products'}</Typography>
        </DialogHeader>

        <DialogContent>
          <DialogContentText>
            {isArchivedTab
              ? 'Are you sure you want to restore the selected products?'
              : 'Are you sure you want to archive the selected products?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleArchiveClick} color="primary" variant="contained">
            {isArchivedTab ? 'Restore' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};

export default BulkActionBar;
